import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import Constants from '../constants/constants';
import { AuthUtilityService } from './auth-utility.service';

/**
 * Generic REST API service for making HTTP requests
 * Provides standardized GET, POST, PUT, and DELETE methods
 * Works with the identifier interceptor for automatic request tracking
 */
@Injectable({
  providedIn: 'root'
})
export class RestApiService {
  constructor(
    private http: HttpClient,
    private authUtility: AuthUtilityService
  ) { }

  /**
   * Performs a GET request
   * @param path - API endpoint path (can be relative or absolute URL)
   * @param params - Query parameters (optional)
   * @param headers - Additional headers (optional)
   * @returns Observable of the response
   */
  get<T = any>(
    path: string,
    params?: { [key: string]: string | number | boolean },
    headers?: HttpHeaders
  ): Observable<T> {
    const url = this.authUtility.buildUrl(path);
    const httpParams = this.authUtility.buildParams(params);
    const requestHeaders = this.authUtility.mergeHeaders(headers);

    return this.http.get<T>(url, {
      headers: requestHeaders,
      params: httpParams
    }).pipe(
      timeout(Constants.HTTP_TIMEOUT), // Set request timeout
      catchError(this.authUtility.handleError)
    );
  }

  /**
   * Performs a POST request
   * @param path - API endpoint path (can be relative or absolute URL)
   * @param body - Request body data
   * @param headers - Additional headers (optional)
   * @returns Observable of the response
   */
  post<T = any>(
    path: string,
    body: any = {},
    headers?: HttpHeaders
  ): Observable<T> {
    const url = this.authUtility.buildUrl(path);
    const requestHeaders = this.authUtility.mergeHeaders(headers);
    return this.http.post<T>(url, body, {
      headers: requestHeaders
    }).pipe(
      timeout(Constants.HTTP_TIMEOUT), // Set request timeout
      catchError(this.authUtility.handleError)
    );
  }

  /**
   * Performs a PUT request
   * @param path - API endpoint path (can be relative or absolute URL)
   * @param body - Request body data
   * @param headers - Additional headers (optional)
   * @returns Observable of the response
   */
  put<T = any>(
    path: string,
    body: any = {},
    headers?: HttpHeaders
  ): Observable<T> {
    const url = this.authUtility.buildUrl(path);
    const requestHeaders = this.authUtility.mergeHeaders(headers);
    return this.http.put<T>(url, body, {
      headers: requestHeaders
    }).pipe(
      timeout(Constants.HTTP_TIMEOUT), // Set request timeout
      catchError(this.authUtility.handleError)
    );
  }

  /**
   * Performs a PATCH request
   * @param path - API endpoint path (can be relative or absolute URL)
   * @param body - Request body data
   * @param headers - Additional headers (optional)
   * @returns Observable of the response
   */
  patch<T = any>(
    path: string,
    body: any = {},
    headers?: HttpHeaders
  ): Observable<T> {
    const url = this.authUtility.buildUrl(path);
    const requestHeaders = this.authUtility.mergeHeaders(headers);
    return this.http.patch<T>(url, body, {
      headers: requestHeaders
    }).pipe(
      timeout(Constants.HTTP_TIMEOUT), // Set request timeout
      catchError(this.authUtility.handleError)
    );
  }

  /**
   * Performs a DELETE request
   * @param path - API endpoint path (can be relative or absolute URL)
   * @param headers - Additional headers (optional)
   * @returns Observable of the response
   */
  delete<T = any>(
    path: string,
    headers?: HttpHeaders
  ): Observable<T> {
    const url = this.authUtility.buildUrl(path);
    const requestHeaders = this.authUtility.mergeHeaders(headers);
    return this.http.delete<T>(url, {
      headers: requestHeaders
    }).pipe(
      timeout(Constants.HTTP_TIMEOUT), // Set request timeout
      catchError(this.authUtility.handleError)
    );
  }
}
